import Layout from '../components/layout/Layout';
import Seo from '../components/common/Seo';
import { SITE_URL } from '../data/business';
import { motion } from 'framer-motion';

const UniversityPage = () => {
  // The single business entity lives on the home page. This page adds only its
  // place in the site hierarchy — a second Restaurant node for the same address
  // would compete with it.
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Thai Food Near UW', item: `${SITE_URL}/near-uw` },
    ],
  };

  const campus = {
    name: "University of Washington",
    address: "Seattle, WA 98195",
    distance: "Approximately 10 minutes from campus",
    directions: "Take the 45 bus heading north from campus, or a quick 8-minute drive up Roosevelt Way."
  };

  return (
    <>
      <Seo
        title="Thai Restaurant Near UW | Thaprachan Thai Cuisine"
        description="Authentic Thai food a 10-minute drive from the University of Washington. Thaprachan Thai Cuisine, 805 NE 65th St, Seattle. Order online or call."
        path="/near-uw"
        schemas={[breadcrumbSchema]}
      />
      
      <Layout>
        {/* Hero Section */}
        <section className="relative py-32 bg-primary/80">
          <div className="container mx-auto px-4 text-center text-white">
            <motion.h1 
              className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Best Thai Restaurant Near University of Washington
            </motion.h1>
            <motion.p 
              className="text-xl md:text-2xl max-w-3xl mx-auto mb-8 opacity-90"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Authentic Thai cuisine just minutes from the UW campus
            </motion.p>
            <motion.div
              className="flex flex-wrap justify-center gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <a
                href="tel:+12065233938"
                data-track="call_click"
                className="px-8 py-3 bg-white text-primary font-medium rounded-sm hover:bg-opacity-90 transition-colors"
              >
                Make a Reservation
              </a>
              <a
                href="https://order.thaprachanthaicuisine.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-track="order_click"
                className="px-8 py-3 bg-secondary text-white font-medium rounded-sm hover:bg-opacity-90 transition-colors"
              >
                Order Online
              </a>
            </motion.div>
          </div>
        </section>
        
        {/* UW Specific Content */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div 
                className="text-center mb-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-heading text-3xl md:text-4xl text-secondary mb-4">Convenient to University of Washington</h2>
                <p className="text-lg text-text">
                  At Thaprachan Thai, we're proud to serve the University of Washington community with delicious, authentic Thai cuisine that's perfect for quick lunches between classes or relaxing dinners after a long day on campus.
                </p>
              </motion.div>
              
              {/* Campus Info */}
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-elegant mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h3 className="font-heading text-2xl text-secondary mb-4">Getting Here From {campus.name}</h3>
                <p className="text-text mb-2"><strong>Distance:</strong> {campus.distance}</p>
                <p className="text-text mb-4"><strong>Directions:</strong> {campus.directions}</p>
                <p className="text-text">
                  We're conveniently located just a short distance from the UW campus, making us the perfect choice when you're craving authentic Thai flavors without having to venture far from campus.
                </p>
              </motion.div>
              
              {/* Call to Action */}
              <motion.div 
                className="bg-primary/10 p-8 rounded-lg text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <h3 className="font-heading text-2xl text-secondary mb-4">Experience the Best Thai Food Near UW</h3>
                <p className="text-lg text-text mb-6">
                  Join the many University of Washington students and faculty who have made Thaprachan their go-to spot for authentic Thai cuisine in Seattle.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a
                    href="https://maps.google.com/?q=805+NE+65th+St,+Seattle,+WA+98115"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 bg-primary text-white font-medium rounded-sm hover:bg-primary/90 transition-colors"
                  >
                    Get Directions from UW
                  </a>
                  <a
                    href="tel:+12065233938"
                    data-track="call_click"
                    className="px-6 py-3 border border-primary text-primary font-medium rounded-sm hover:bg-primary/10 transition-colors"
                  >
                    Make a Reservation
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default UniversityPage; 